import { motion } from 'framer-motion';
import { Gift, Trophy, Clock, CheckCircle, Lock, MapPin, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Prize } from '@/hooks/usePrizes';
import { format, differenceInDays, endOfMonth } from 'date-fns';
import { es } from 'date-fns/locale';

interface PrizeTimelineCardProps {
  prize: Prize;
  isCurrentMonth: boolean;
  isWinner: boolean;
  currentLeaderName?: string;
  onClaim?: () => void;
  formatMonthYear: (monthYear: string) => string;
}

export function PrizeTimelineCard({
  prize,
  isCurrentMonth,
  isWinner,
  currentLeaderName,
  onClaim,
  formatMonthYear,
}: PrizeTimelineCardProps) {
  const getStatusConfig = () => {
    if (isCurrentMonth && prize.status === 'pending') {
      return {
        icon: Clock,
        label: 'En curso',
        bgColor: 'bg-slate-800/80',
        borderColor: 'border-amber-500/40',
        iconBg: 'bg-amber-500/20',
        iconColor: 'text-amber-400',
        badgeClass: 'bg-amber-500/20 text-amber-300 border border-amber-500/30',
      };
    }
    
    if (prize.status === 'claimable' && isWinner) {
      return {
        icon: Gift,
        label: '¡Reclámalo!',
        bgColor: 'bg-gradient-to-br from-green-900/50 to-emerald-900/50',
        borderColor: 'border-green-500/50',
        iconBg: 'bg-green-500/20',
        iconColor: 'text-green-400',
        badgeClass: 'bg-green-500 text-white animate-pulse',
      };
    }
    
    if (prize.status === 'claimable') {
      return {
        icon: Trophy,
        label: 'Pendiente',
        bgColor: 'bg-slate-800/80',
        borderColor: 'border-yellow-500/40',
        iconBg: 'bg-yellow-500/20',
        iconColor: 'text-yellow-400',
        badgeClass: 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30',
      };
    }
    
    if (prize.status === 'claimed') {
      return {
        icon: CheckCircle,
        label: 'Entregado',
        bgColor: 'bg-slate-900/60',
        borderColor: 'border-zinc-700/50',
        iconBg: 'bg-zinc-700/50',
        iconColor: 'text-zinc-400',
        badgeClass: 'bg-zinc-700/50 text-zinc-400 border border-zinc-600/30',
      };
    }
    
    return {
      icon: Lock,
      label: 'Pendiente',
      bgColor: 'bg-slate-900/60',
      borderColor: 'border-zinc-700/50',
      iconBg: 'bg-zinc-700/50',
      iconColor: 'text-zinc-500',
      badgeClass: 'bg-zinc-700/50 text-zinc-500 border border-zinc-600/30',
    };
  };

  const config = getStatusConfig();
  const StatusIcon = config.icon;

  const getDaysRemaining = () => {
    if (!isCurrentMonth) return null;
    const [year, month] = prize.month_year.split('-');
    const endDate = endOfMonth(new Date(parseInt(year), parseInt(month) - 1, 1));
    return differenceInDays(endDate, new Date());
  };

  const daysRemaining = getDaysRemaining();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`relative rounded-xl border ${config.borderColor} ${config.bgColor} p-5 overflow-hidden backdrop-blur-sm`}
    >
      {/* Glow effect for claimable prizes */}
      {prize.status === 'claimable' && isWinner && (
        <div className="absolute inset-0 bg-gradient-to-r from-green-500/5 via-emerald-500/10 to-green-500/5 animate-pulse" />
      )}

      <div className="relative z-10">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`p-2.5 rounded-lg ${config.iconBg}`}>
              <Calendar className={`h-5 w-5 ${config.iconColor}`} />
            </div>
            <div>
              <h3 className="font-bold text-lg text-white">
                Premio del Mes - <span className="capitalize">{formatMonthYear(prize.month_year)}</span>
              </h3>
              {daysRemaining !== null && daysRemaining > 0 && (
                <p className="text-sm text-amber-400 mt-0.5">
                  ⏳ {daysRemaining} días restantes
                </p>
              )}
            </div>
          </div>
          <Badge className={`${config.badgeClass} flex items-center gap-1.5 px-3 py-1`}>
            <StatusIcon className="h-3.5 w-3.5" />
            {config.label}
          </Badge>
        </div>

        {/* Prize Info */}
        <div className="bg-black/30 rounded-lg p-4 mb-4 border border-white/5">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-500/20">
              <Gift className="h-5 w-5 text-amber-400" />
            </div>
            <div>
              <span className="font-semibold text-white text-lg">{prize.prize_name}</span>
              {prize.prize_description && (
                <p className="text-sm text-zinc-400 mt-0.5">{prize.prize_description}</p>
              )}
            </div>
          </div>
        </div>

        {/* Winner/Leader Info */}
        {isCurrentMonth && prize.status === 'pending' && currentLeaderName && (
          <div className="flex items-center gap-2 text-sm text-zinc-300 mb-4 bg-amber-500/10 rounded-lg p-3 border border-amber-500/20">
            <Trophy className="h-4 w-4 text-amber-400" />
            <span>Líder actual: <span className="font-semibold text-amber-300">{currentLeaderName}</span></span>
            <span className="text-zinc-500">(puede cambiar)</span>
          </div>
        )}

        {prize.winner_name && !isCurrentMonth && (
          <div className="space-y-2 mb-4 bg-white/5 rounded-lg p-3">
            <div className="flex items-center gap-2 text-sm text-zinc-300">
              <Trophy className="h-4 w-4 text-amber-400" />
              <span>Ganador: <span className="font-semibold text-white">{prize.winner_name}</span></span>
            </div>
            {prize.winner_ubicacion && (
              <div className="flex items-center gap-2 text-sm text-zinc-400 ml-6">
                <MapPin className="h-3.5 w-3.5" />
                <span>{prize.winner_ubicacion}</span>
              </div>
            )}
            {prize.claimed_at && (
              <div className="flex items-center gap-2 text-sm text-zinc-500 ml-6">
                <CheckCircle className="h-3.5 w-3.5 text-green-500" />
                <span>Reclamado: {format(new Date(prize.claimed_at), "d MMM yyyy", { locale: es })}</span>
              </div>
            )}
          </div>
        )}

        {/* Claim Button */}
        {prize.status === 'claimable' && isWinner && onClaim && (
          <Button
            onClick={onClaim}
            className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-bold shadow-lg shadow-green-500/20"
          >
            <Gift className="h-4 w-4 mr-2" />
            🎉 Reclamar Mi Premio
          </Button>
        )}
      </div>
    </motion.div>
  );
}
